import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    DotsThreeOutlineVerticalIcon,
    FilePdfIcon,
    MoonIcon,
    QuestionIcon,
    RobotIcon,
    SignOutIcon,
    SunIcon,
    TableIcon,
    UserCircleGearIcon,
    UserCircleIcon,
    VideoIcon,
} from '@phosphor-icons/react';
import { Avatar, Button, Dropdown } from 'antd';
import type { MenuInfo } from 'rc-menu/lib/interface';
import TrinityLogo from '../../../../assets/img/logos/Trinity_Solar_Logo_2025_-_Reversed_Full_Color_-_Web.svg';
import OneButtonLogo from '../../../../assets/img/logos/one_button_logo.png';
import oneDRAWWhiteLogo from '../../../../assets/img/logos/one_draw_logo_white.png';
import oneDRAWDarkLogo from '../../../../assets/img/logos/one_draw_logo.png';
import Pdf from '../../../../assets/PDF/oneDRAW User Guide (Latest).pdf';
import { ThemeContext } from '../../../../contexts/ThemeContext';
import { useCurrentUser } from '../../../../contexts/UserContext';
import { useViewerStore } from '../../../../store/store';
import getMsalInstance from '../../../../helpers/getMsalInstance';
import azureBLOBStorageService from '../../../../services/azureBLOBStorageService';
import UserSettings from './UserSettings';
import {
    persistDashboardLayoutVariant,
    type DashboardLayoutVariant,
} from '../../../../helpers/dashboardLayoutVariant';
import '../../Editor-Layout/oneBUTTONDashboard/oneButton-css/OneButtonDashboard.css';

const AppUnifiedHeader: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, hasCapability } = useCurrentUser();
    const { theme: appTheme, toggleTheme: toggleAppTheme } = useContext(ThemeContext);

    const headerTheme: 'dark' | 'light' = appTheme === 'light' ? 'light' : 'dark';

    const toggleHeaderTheme = useCallback(() => {
        toggleAppTheme();
    }, [toggleAppTheme]);

    const [username, setUsername] = useState('User');
    const [isUserSettingsModalOpen, setIsUserSettingsModalOpen] = useState(false);
    const [isMobileHeader, setIsMobileHeader] = useState<boolean>(() => window.innerWidth <= 540);

    useEffect(() => {
        setUsername(user?.user_name ?? 'User');
    }, [user]);

    useEffect(() => {
        const fetchProfilePhoto = async () => {
            if (user?.user_Id) {
                const fileName = `${user.user_Id}.png`;
                const exists = await azureBLOBStorageService.checkFileExist(fileName);
                if (exists) {
                    const response = await azureBLOBStorageService.getFile(fileName);
                    if (response) {
                        useViewerStore.setState({ isProfilePhotoAvailable: true });
                        useViewerStore.setState({ userProfilePhoto: URL.createObjectURL(response) });
                    }
                }
            }
        };
        fetchProfilePhoto();
    }, [user]);

    useEffect(() => {
        const handleViewportResize = () => {
            setIsMobileHeader(window.innerWidth <= 540);
        };

        window.addEventListener('resize', handleViewportResize);
        return () => {
            window.removeEventListener('resize', handleViewportResize);
        };
    }, []);

    const isAdmin =
        user?.application_role_name === 'Admin' ||
        user?.application_role_name === 'Sales Ops' ||
        user?.application_role_name === 'RSA';
    const isAdminUser = user?.application_role_name === 'Admin';

    const instance = getMsalInstance();
    const isProfilePhotoAvailable = useViewerStore((s) => s.isProfilePhotoAvailable);
    const userProfilePhoto = useViewerStore((s) => s.userProfilePhoto);
    const dashboardLayoutVariant = useViewerStore((s) => s.dashboardLayoutVariant);

    const showDashboardVersionToggle =
        isAdminUser &&
        hasCapability('Show_OneButton') &&
        location.pathname.startsWith('/configure');

    const setDashboardLayoutVariant = useCallback((variant: DashboardLayoutVariant) => {
        persistDashboardLayoutVariant(variant);
        useViewerStore.setState({ dashboardLayoutVariant: variant });
    }, []);

    const dashboardVersionToggle = showDashboardVersionToggle ? (
        <div
            className="app-unified-header__user-dropdown-version"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
        >
            <span className="app-unified-header__user-dropdown-version-label">Dashboard version</span>
            <div
                className="app-unified-header__version-toggle app-unified-header__version-toggle--menu obd-metrics__action-group"
                role="group"
                aria-label="Dashboard version"
            >
                <button
                    type="button"
                    className={`app-unified-header__nav-link obd-merged-system__chip-btn app-unified-header__version-btn${dashboardLayoutVariant === 'v2' ? ' app-unified-header__nav-link--active' : ''}`}
                    aria-pressed={dashboardLayoutVariant === 'v2'}
                    onClick={() => setDashboardLayoutVariant('v2')}
                >
                    V2
                </button>
                <button
                    type="button"
                    className={`app-unified-header__nav-link obd-merged-system__chip-btn app-unified-header__version-btn${dashboardLayoutVariant === 'v3' ? ' app-unified-header__nav-link--active' : ''}`}
                    aria-pressed={dashboardLayoutVariant === 'v3'}
                    onClick={() => setDashboardLayoutVariant('v3')}
                >
                    V3
                </button>
            </div>
        </div>
    ) : null;

    const handleSignOut = () => {
        sessionStorage.setItem('loginMessage', 'Login with Microsoft');
        localStorage.clear();
        instance.logoutRedirect();
    };

    const menuItems = [
        {
            key: '1',
            label: (
                <a href="#" onClick={() => setIsUserSettingsModalOpen(true)}>
                    User Settings
                </a>
            ),
            icon: <UserCircleGearIcon size={24} weight="fill" />,
        },
        // ...(showDashboardVersionToggle
        //     ? [
        //           {
        //               key: 'dashboard-version',
        //               label: dashboardVersionToggle,
        //               className: 'app-unified-header__user-menu-item--version',
        //               style: { cursor: 'default' as const },
        //           },
        //       ]
        //     : []),
        {
            key: '2',
            label: <a onClick={handleSignOut}>Sign Out</a>,
            icon: <SignOutIcon size={24} weight="fill" />,
        },
    ];

    const onHelpMenuClick = (info: MenuInfo) => {
        if (info.key === 'userGuide') {
            window.open(Pdf, '_blank');
        } else if (info.key === 'trainingVideos') {
            navigate('/training');
        }
    };

    const helpItems = [
        { label: 'User Guide', key: 'userGuide', icon: <FilePdfIcon size={24} weight="bold" /> },
        { label: 'Training Videos', key: 'trainingVideos', icon: <VideoIcon size={24} weight="bold" /> },
    ];

    const overflowItems = [
        ...(hasCapability('Sites_Table')
            ? [{ label: 'Sites', key: 'goSites', icon: <TableIcon size={18} weight="bold" /> }]
            : []),
        ...(isAdmin
            ? [{ label: 'Batch AI', key: 'goBatchAi', icon: <RobotIcon size={18} weight="bold" /> }]
            : []),
        ...(isMobileHeader
            ? [
                  { label: 'User Settings', key: 'userSettings', icon: <UserCircleGearIcon size={18} weight="bold" /> },
                  ...(showDashboardVersionToggle
                      ? [
                            {
                                label: `Dashboard V2${dashboardLayoutVariant === 'v2' ? ' (active)' : ''}`,
                                key: 'dashV2',
                            },
                            {
                                label: `Dashboard V3${dashboardLayoutVariant === 'v3' ? ' (active)' : ''}`,
                                key: 'dashV3',
                            },
                        ]
                      : []),
                  {
                      label: headerTheme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode',
                      key: 'toggleTheme',
                      icon: headerTheme === 'dark' ? <SunIcon size={18} weight="bold" /> : <MoonIcon size={18} weight="bold" />,
                  },
                  { label: 'Sign Out', key: 'signOut', icon: <SignOutIcon size={18} weight="bold" /> },
              ]
            : []),
        { label: 'Help - User Guide', key: 'userGuide', icon: <FilePdfIcon size={18} weight="bold" /> },
        { label: 'Help - Training Videos', key: 'trainingVideos', icon: <VideoIcon size={18} weight="bold" /> },
    ];

    const onOverflowMenuClick = (info: MenuInfo) => {
        if (info.key === 'goSites') {
            navigate('/sites');
            return;
        }
        if (info.key === 'goBatchAi') {
            navigate('/batch-ai');
            return;
        }
        if (info.key === 'userSettings') {
            setIsUserSettingsModalOpen(true);
            return;
        }
        if (info.key === 'dashV2') {
            setDashboardLayoutVariant('v2');
            return;
        }
        if (info.key === 'dashV3') {
            setDashboardLayoutVariant('v3');
            return;
        }
        if (info.key === 'toggleTheme') {
            toggleHeaderTheme();
            return;
        }
        if (info.key === 'signOut') {
            handleSignOut();
            return;
        }
        onHelpMenuClick(info);
    };

    const oneDrawMarkSrc = headerTheme === 'dark' ? oneDRAWWhiteLogo : oneDRAWDarkLogo;
    const showOneButton = hasCapability('Show_OneButton');

    return (
        <>
            <div className={`app-unified-header obd-theme-${headerTheme}`}>
                <header className="editor-dashboard-header app-unified-header__bar">
                    <div className="app-unified-header__brand">
                        <Link to="/sites">
                            <img
                                src={TrinityLogo}
                                alt="Trinity Solar"
                                className="obd-head__logo-img"
                                style={{ height: 32, objectFit: 'contain' }}
                            />
                        </Link>
                        {showOneButton && (
                            <span
                                className="obd-head__badge"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    paddingInline: 10,
                                }}
                            >
                                <img
                                    src={OneButtonLogo}
                                    alt="oneBUTTON"
                                    style={{
                                        height: 24,
                                        objectFit: 'contain',
                                        backgroundColor: '#ffffff',
                                        borderRadius: 999,
                                        padding: '2px 10px',
                                    }}
                                />
                                <span
                                    style={{
                                        fontSize: 10,
                                        fontWeight: 700,
                                        padding: '2px 6px',
                                        borderRadius: 999,
                                        backgroundColor: 'rgba(15,23,42,0.9)',
                                        color: '#facc15',
                                        letterSpacing: 0.6,
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    Pro
                                </span>
                            </span>
                        )}
                        <span className="app-unified-header__powered">
                            {showOneButton && (
                                <span className="app-unified-header__powered-text">powered by</span>
                            )}
                            <img src={oneDrawMarkSrc} alt="oneDRAW" className="app-unified-header__onedraw-mark" />
                        </span>
                    </div>

                    <nav className="app-unified-header__nav" aria-label="Main">
                        <div
                            className="app-unified-header__nav-actions obd-metrics__action-group"
                            role="group"
                            aria-label="Main navigation"
                        >
                            {hasCapability('Sites_Table') && (
                                <button
                                    type="button"
                                    className={`obd-merged-system__chip-btn app-unified-header__nav-chip${
                                        location.pathname === '/sites' || location.pathname.startsWith('/sites/')
                                            ? ' app-unified-header__nav-chip--active'
                                            : ''
                                    }`}
                                    onClick={() => navigate('/sites')}
                                >
                                    <TableIcon size={16} weight="bold" aria-hidden />
                                    <span>Sites</span>
                                </button>
                            )}
                            {hasCapability('Show_Batch_AI') && (
                                <button
                                    type="button"
                                    className={`obd-merged-system__chip-btn app-unified-header__nav-chip${
                                        location.pathname === '/batch-ai' || location.pathname.startsWith('/batch-ai/')
                                            ? ' app-unified-header__nav-chip--active'
                                            : ''
                                    }`}
                                    onClick={() => navigate('/batch-ai')}
                                >
                                    <RobotIcon size={16} weight="bold" aria-hidden />
                                    <span>Batch AI</span>
                                </button>
                            )}
                            <Dropdown menu={{ items: helpItems, onClick: onHelpMenuClick }}>
                                <button type="button" className="obd-merged-system__chip-btn app-unified-header__nav-chip">
                                    <QuestionIcon size={16} weight="bold" aria-hidden />
                                    <span>Help</span>
                                </button>
                            </Dropdown>
                        </div>
                    </nav>

                    <div className="app-unified-header__actions">
                        <Dropdown
                            menu={{ items: overflowItems, onClick: onOverflowMenuClick }}
                            trigger={['click']}
                            overlayStyle={{ zIndex: 12050 }}
                        >
                            <button
                                type="button"
                                className="app-unified-header__overflow-btn"
                                aria-label="More navigation options"
                                title="More"
                            >
                                <DotsThreeOutlineVerticalIcon size={18} weight="bold" />
                            </button>
                        </Dropdown>
                        <Dropdown menu={{ items: menuItems }} overlayStyle={{ zIndex: 12050 }}>
                            <Button
                                icon={
                                    isProfilePhotoAvailable ? (
                                        <Avatar src={<img src={userProfilePhoto ?? ''} alt="" />} size="small" />
                                    ) : (
                                        <UserCircleIcon size={22} weight="fill" />
                                    )
                                }
                                size="middle"
                                type="text"
                                onClick={(e) => e.preventDefault()}
                                className="app-unified-header__user-btn"
                            >
                                <span className="app-unified-header__username">{username !== '' ? username : 'User'}</span>
                            </Button>
                        </Dropdown>
                        <button
                            type="button"
                            onClick={toggleHeaderTheme}
                            className="obd-theme-toggle"
                            aria-label={headerTheme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
                            title={headerTheme === 'dark' ? 'Light mode' : 'Dark mode'}
                        >
                            {headerTheme === 'dark' ? <SunIcon size={20} weight="duotone" /> : <MoonIcon size={20} weight="duotone" />}
                        </button>
                    </div>
                </header>
            </div>
            <UserSettings setIsUserSettingsModalOpen={setIsUserSettingsModalOpen} isUserSettingsModalOpen={isUserSettingsModalOpen} />
        </>
    );
};

export default AppUnifiedHeader;
